import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { PROVINCES, getDistricts } from "@/data/turkiye";

const today = () => new Date().toISOString().slice(0, 10);

export default function CustomerForm({ open, onOpenChange, columns, onCreated }) {
  const [values, setValues] = useState({});
  const [saving, setSaving] = useState(false);

  // reset with defaults each time the card opens
  useEffect(() => {
    if (open) {
      const init = {};
      columns.forEach((c) => {
        if (c.key === "tarih") init[c.key] = today();
      });
      setValues(init);
    }
  }, [open, columns]);

  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const fields = columns.filter((c) => c.key !== "id");

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/records", { values });
      toast.success("Müşteri kartı eklendi");
      onCreated(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  const renderField = (col) => {
    const val = values[col.key] ?? "";
    if (col.key === "il" || col.key === "ilce") {
      const opts = col.key === "il" ? PROVINCES : getDistricts(values.il);
      return (
        <Select
          value={val}
          onValueChange={(v) => {
            if (col.key === "il") setValues((s) => ({ ...s, il: v, ilce: "" }));
            else set(col.key, v);
          }}
          disabled={col.key === "ilce" && !values.il}
        >
          <SelectTrigger data-testid={`form-field-${col.key}`}>
            <SelectValue placeholder={col.key === "ilce" && !values.il ? "Önce il seçin" : "Seçiniz"} />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            {opts.map((o) => (
              <SelectItem key={o} value={o}>
                {o}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (col.type === "select") {
      return (
        <Select value={val} onValueChange={(v) => set(col.key, v)}>
          <SelectTrigger data-testid={`form-field-${col.key}`}>
            <SelectValue placeholder="Seçiniz" />
          </SelectTrigger>
          <SelectContent>
            {(col.options || []).map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.value}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }
    if (col.type === "textarea") {
      return (
        <Textarea
          value={val}
          onChange={(e) => set(col.key, e.target.value)}
          rows={2}
          data-testid={`form-field-${col.key}`}
        />
      );
    }
    const inputType = col.type === "number" ? "number" : col.type === "date" ? "date" : "text";
    return (
      <Input
        type={inputType}
        value={val}
        onChange={(e) => set(col.key, e.target.value)}
        data-testid={`form-field-${col.key}`}
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col" data-testid="customer-form">
        <DialogHeader>
          <DialogTitle>Yeni Müşteri Kartı</DialogTitle>
          <DialogDescription>
            Sütunlardaki bilgileri doldurun. ID ve Tarih otomatik oluşturulur.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto grid-scroll grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-3 pr-1">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground flex items-center gap-1">
              ID <Sparkles className="w-3 h-3" />
            </Label>
            <Input value="Otomatik" disabled className="text-muted-foreground" />
          </div>
          {fields.map((col) => (
            <div key={col.key} className="space-y-1.5">
              <Label className="text-sm flex items-center gap-1">
                {col.label}
                {col.key === "tarih" && <Sparkles className="w-3 h-3 text-primary" />}
              </Label>
              {renderField(col)}
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            İptal
          </Button>
          <Button onClick={save} disabled={saving} data-testid="save-customer-button">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Kaydet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
